/**
 * Todo Work Specs Section Component
 * Displays related work specs with edit/preview actions
 * Supports promotion pattern with navigation depth (list → preview)
 */

import { useState } from 'react';
import { FileText, Maximize2, Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SecondaryPanelContent, SecondaryPanelOpenButton } from '@/components/layout/secondary-panel-content';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { fetchWithAuth } from '@/lib/api';
import { cn } from '@/lib/utils';

interface RelatedDocument {
  id: string;
  title: string;
  type: string;
  status: string;
  excerpt: string;
  tags: string[];
  projectName: string;
  updatedAt: string;
  filePath: string;
  matchScore?: number;
  primary?: boolean;
  documentIndex?: number | null;
  documentRole?: string | null;
}

interface TodoWorkSpecsSectionProps {
  relatedDocs: RelatedDocument[];
  isLoadingDocs: boolean;
  onDocumentClick?: (documentId: string, openInNewTab?: boolean | React.MouseEvent) => Promise<void>;
  onPreviewDocument?: (filePath: string, title?: string) => void;
  onDemote?: () => void;
  openTabs?: Array<{ id: string; isPinned?: boolean }>;
  activeTabId?: string | null;
}

export function TodoWorkSpecsSection({
  relatedDocs,
  isLoadingDocs,
  onDocumentClick,
  onPreviewDocument,
  onDemote,
  openTabs = [],
  activeTabId = null
}: TodoWorkSpecsSectionProps) {
  const [selectedSpec, setSelectedSpec] = useState<RelatedDocument | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewProjectName, setPreviewProjectName] = useState<string | null>(null);

  // Sort relatedDocs by documentIndex (numerically, not lexicographically)
  // Items with lower documentIndex come first (1 before 11)
  // Items with null/undefined documentIndex come last
  // README.md always comes first (Priority 0)
  const sortedRelatedDocs = [...relatedDocs].sort((a, b) => {
    // README.md always first
    const aIsReadme = a.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
    const bIsReadme = b.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
    if (aIsReadme && !bIsReadme) return -1;
    if (!aIsReadme && bIsReadme) return 1;

    const aIndex = a.documentIndex;
    const bIndex = b.documentIndex;

    // Both have valid indices - sort numerically
    if (aIndex != null && bIndex != null) {
      return aIndex - bIndex;
    }

    // Only a has an index - a comes first
    if (aIndex != null && bIndex == null) {
      return -1;
    }

    // Only b has an index - b comes first
    if (aIndex == null && bIndex != null) {
      return 1;
    }

    // Neither has an index - maintain original order
    return 0;
  });

  // Identify primary work spec using priority:
  // 0. filename is README.md (escalated todo primary convention)
  // 1. primary: true in frontmatter
  // 2. document_role: "index"
  // 3. lowest document_index (1 is better than 11)
  // 4. highest matchScore (fallback)
  const primarySpec = sortedRelatedDocs.length > 0
    ? sortedRelatedDocs.reduce((prev, current) => {
        // Priority 0: filename is README.md (escalated todo primary convention)
        const currentIsReadme = current.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
        const prevIsReadme = prev.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
        if (currentIsReadme && !prevIsReadme) return current;
        if (!currentIsReadme && prevIsReadme) return prev;

        // Priority 1: primary flag
        if (current.primary && !prev.primary) return current;
        if (!current.primary && prev.primary) return prev;

        // Priority 2: document_role === "index"
        if (current.documentRole === 'index' && prev.documentRole !== 'index') return current;
        if (prev.documentRole === 'index' && current.documentRole !== 'index') return prev;

        // Priority 3: lowest document_index (1 < 11)
        if (current.documentIndex !== null && current.documentIndex !== undefined) {
          if (prev.documentIndex === null || prev.documentIndex === undefined) return current;
          if (current.documentIndex < prev.documentIndex) return current;
          if (prev.documentIndex < current.documentIndex) return prev;
        } else if (prev.documentIndex !== null && prev.documentIndex !== undefined) {
          return prev;
        }

        // Priority 4: highest matchScore (fallback)
        return (current.matchScore || 0) > (prev.matchScore || 0) ? current : prev;
      })
    : null;

  const handleSpecClick = async (spec: RelatedDocument) => {
    setSelectedSpec(spec);
    setIsLoadingPreview(true);
    setPreviewContent('');
    setPreviewProjectName(null);

    // Fetch actual document content
    try {
      const apiUrl = `/api/documents/by-path?path=${encodeURIComponent(spec.filePath)}`;
      const response = await fetchWithAuth(apiUrl);

      if (!response.ok) {
        throw new Error('Failed to load document');
      }

      const doc = await response.json();

      // Extract project name from frontmatter or file path
      let projectName = null;
      if (doc.frontmatter?.project?.name) {
        projectName = doc.frontmatter.project.name;
      } else if (doc.frontmatter?.project_name) {
        projectName = doc.frontmatter.project_name;
      } else if (spec.filePath) {
        // Extract from file path: clients/greater-good/... -> Greater Good
        const pathMatch = spec.filePath.match(/clients\/([^\/]+)/);
        if (pathMatch) {
          // Convert kebab-case to Title Case
          projectName = pathMatch[1]
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      }

      setPreviewProjectName(projectName);
      setPreviewContent(doc.content || '');
    } catch (error) {
      console.error('[TodoWorkSpecsSection] Error loading preview:', error);
      setPreviewContent(`# ${spec.title}\n\n**Error loading document preview.**\n\nPlease use the "Open" button to view the full document.`);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleBackToList = () => {
    setSelectedSpec(null);
  };

  const handleOpenDocument = async (spec: RelatedDocument, event?: React.MouseEvent) => {
    if (onDocumentClick && spec.filePath) {
      // Check if current tab is pinned
      const currentTab = openTabs.find(tab => tab.id === activeTabId);
      const isCurrentTabPinned = currentTab?.isPinned === true;

      // If current tab is pinned, open in new tab (next to pinned tab)
      // If current tab is NOT pinned, replace current tab (unless CMD+Click)
      // CMD+Click always opens in new tab
      const openInNewTab = event ? (event.metaKey || event.ctrlKey) : isCurrentTabPinned;

      const syntheticEvent = openInNewTab ? {
        metaKey: true,
        ctrlKey: false,
        preventDefault: () => {},
        stopPropagation: () => {}
      } : undefined;

      await onDocumentClick(spec.filePath, syntheticEvent as any);
    }
  };

  // Format project name safely
  const formatProjectName = (projectName: string | undefined): string | null => {
    if (!projectName) return null;
    if (typeof projectName === 'string' && projectName.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(projectName);
        return parsed.name || parsed.title || null;
      } catch (e) {
        return null;
      }
    }
    return projectName;
  };

  // Extract project name from file path
  const extractProjectFromPath = (filePath: string): string | null => {
    const pathMatch = filePath.match(/clients\/([^\/]+)/);
    if (pathMatch) {
      // Convert kebab-case to Title Case
      return pathMatch[1]
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return null;
  };

  return (
    <SecondaryPanelContent
      title={selectedSpec ? undefined : 'Work Specs'}
      breadcrumbs={
        selectedSpec
          ? [
              { label: 'Work Specs', onClick: handleBackToList },
              { label: selectedSpec.title },
            ]
          : undefined
      }
      onClose={selectedSpec ? handleBackToList : onDemote}
      loading={isLoadingDocs}
      headerActions={
        selectedSpec ? (
          <SecondaryPanelOpenButton
            onClick={() => {
              void handleOpenDocument(selectedSpec);
            }}
          />
        ) : undefined
      }
    >
      {selectedSpec ? (
        // Preview View — prose padding lives on MarkdownPreview (panel), not an outer p-6
        <div>
          {isLoadingPreview ? (
            <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              Loading preview…
            </div>
          ) : (
            <MarkdownPreview
              content={previewContent || `# ${selectedSpec.title}\n\nLoading...`}
              className="max-w-none"
              readingColumnPadding="panel"
            />
          )}
        </div>
      ) : (
        // List View — quiet rows matching bookmarks todo preview
        <div className="@container p-4 space-y-1 min-w-0">
          {sortedRelatedDocs.length > 0 ? (
            sortedRelatedDocs.map((spec) => {
              const isPrimary = primarySpec?.id === spec.id;
              const projectName =
                formatProjectName(spec.projectName) ||
                extractProjectFromPath(spec.filePath);

              return (
                <div
                  key={spec.id}
                  className={cn(
                    'w-full px-2.5 py-2 rounded-md transition-colors border group cursor-pointer hover:bg-accent/50',
                    isPrimary
                      ? 'bg-muted/40 border-border border-l-2 border-l-primary'
                      : 'bg-transparent border-transparent hover:border-border',
                  )}
                  onClick={() => handleSpecClick(spec)}
                >
                  <div className="flex flex-col gap-2 @[260px]:flex-row @[260px]:items-start @[260px]:justify-between @[260px]:gap-3 min-w-0">
                    <div className="flex items-start gap-2 flex-1 min-w-0 w-full">
                      <FileText
                        className={cn(
                          'h-3.5 w-3.5 mt-0.5 flex-shrink-0',
                          isPrimary
                            ? 'text-foreground/70'
                            : 'text-muted-foreground',
                        )}
                      />
                      <div className="flex-1 min-w-0 w-full">
                        <div className="flex flex-col gap-1 w-full min-w-0 mb-0.5 @[260px]:flex-row @[260px]:items-start @[260px]:justify-between @[260px]:gap-2">
                          <p
                            className="w-full min-w-0 text-xs font-medium line-clamp-3 break-words text-foreground/90"
                            title={[spec.title, spec.filePath]
                              .filter(Boolean)
                              .join('\n\n')}
                          >
                            {spec.title}
                          </p>
                          {(isPrimary || spec.status) && (
                            <div className="flex flex-wrap items-center gap-1 shrink-0">
                              {isPrimary && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 h-4 font-normal"
                                >
                                  <Star className="h-2 w-2 mr-0.5" />
                                  Primary
                                </Badge>
                              )}
                              {spec.status && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 h-4 font-normal"
                                >
                                  {spec.status}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {spec.excerpt ? (
                          <p
                            className="text-[10px] text-muted-foreground line-clamp-2 break-words"
                            title={spec.excerpt || spec.filePath || undefined}
                          >
                            {spec.excerpt}
                          </p>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                          {projectName && (
                            <span className="text-[10px] text-muted-foreground">
                              {projectName}
                            </span>
                          )}
                          {spec.updatedAt && (
                            <span className="text-[10px] text-muted-foreground">
                              · {spec.updatedAt}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 self-end @[260px]:self-start text-muted-foreground"
                      title="Open in tab"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleOpenDocument(spec, e);
                      }}
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {spec.tags && spec.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5 pl-5">
                      {spec.tags.slice(0, 3).map((tag, i) => (
                        <Badge
                          key={i}
                          variant="secondary"
                          className="text-[9px] px-1 py-0 h-4 font-normal"
                        >
                          {tag}
                        </Badge>
                      ))}
                      {spec.tags.length > 3 && (
                        <Badge
                          variant="secondary"
                          className="text-[9px] px-1 py-0 h-4 font-normal"
                        >
                          +{spec.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-6">
              <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
              <p className="text-sm text-muted-foreground">
                No related work specs found
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Work specs will appear here when they reference this Basecamp
                todo
              </p>
            </div>
          )}
        </div>
      )}
    </SecondaryPanelContent>
  );
}

